import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '../../services/navigation/navigation.service';
import { CacheService } from '../../services/cache/cache.service';
import { CommonService } from '../../services/common/common.service';
import { Column } from '../interfaces/column.interface';
import moment from 'moment';
import { UsersDataService } from './users-data.service';
import { User, UsersResult } from '../interfaces';
import { FitnessGoalsDataService } from './fitness-goals-data.service';
import { FitnessGoal, FitnessGoalsResult } from '../interfaces';
import { SportsDataService } from './sports-data.service';
import { Sport, SportsResult } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class MembersColumnsService {

  public users: User[] = [];
  public fitnessGoals: FitnessGoal[] = [];
  public fitnessGoalsFiltered: FitnessGoal[] = [];
  public sports: Sport[] = [];
  public sportsFiltered: Sport[] = [];
  public defaultParameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private cache: CacheService,
    private common: CommonService,
    private usersDataService: UsersDataService,
    private fitnessGoalsDataService: FitnessGoalsDataService,
    private sportsDataService: SportsDataService,

  ) {
    this.getUsers()
    this.getFitnessGoals()
    this.getSports()
  }

  getUsers(offset: number = 0): void {
    const cacheKey: string = 'users_type-member.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.users = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    parameters['filters[user_.type]'] = 'member';
    this.usersDataService.getUsers(parameters).subscribe((response: UsersResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.users = response.results;
        } else {
          this.users = this.users.concat(response.results);
        }
        if (response.inlineCount > this.users.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getUsers(offset);
        }
        response.results = this.users;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getFitnessGoals(): void {
    const cacheKey: string = 'fitnessGoals.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null) {
      this.fitnessGoals = cache.results
      return;
    }
    this.fitnessGoalsDataService.getFitnessGoals(this.defaultParameters).subscribe((response: FitnessGoalsResult|any) => {
      if (response !== null) {
        this.fitnessGoals = response.results;
        this.fitnessGoalsFiltered = response.results;
        this.cache.set(cacheKey, response);
      }
    });
  }

  filterFitnessGoals(searchText: string) {
    if (!searchText) {
      this.fitnessGoalsFiltered = Object.assign([], this.fitnessGoals);
      return;
    }
    this.fitnessGoalsFiltered = []
    this.fitnessGoals.map(
      (item: any) => {
        if (item.name.toLowerCase().indexOf(searchText.toLowerCase()) > -1) {
          this.fitnessGoalsFiltered.push(item);
        }
      }
    );
  }

  getSports(): void {
    const cacheKey: string = 'sports.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null) {
      this.sports = cache.results
      return;
    }
    this.sportsDataService.getSports(this.defaultParameters).subscribe((response: SportsResult|any) => {
      if (response !== null) {
        this.sports = response.results;
        this.sportsFiltered = response.results;
        this.cache.set(cacheKey, response);
      }
    });
  }

  filterSports(searchText: string) {
    if (!searchText) {
      this.sportsFiltered = Object.assign([], this.sports);
      return;
    }
    this.sportsFiltered = []
    this.sports.map(
      (item: any) => {
        if (item.name.toLowerCase().indexOf(searchText.toLowerCase()) > -1) {
          this.sportsFiltered.push(item);
        }
      }
    );
  }

  getColumns(): Column[] {
    return [
      { field: 'id', sortField: 'id', title: this.translate.instant('ID'), show: (this.navigation.getParamValue('members.id.showFiled', true) && true), displayInList: true, type: 'numericValue'},
      { field: 'name', sortField: 'name', title: this.translate.instant('NAME'), show: (this.navigation.getParamValue('members.name.showFiled', true) && true), displayInList: true, type: 'textValue'},
      { field: 'user', title: this.translate.instant('USER'), type: 'linkValue', filterData: this.users, show: (this.navigation.getParamValue('members.userId.showFiled', true) && true), displayInList: true, filterField: 'user.id', displayField: 'username', sortField: 'user.username', url: 'users' },
      { field: 'description', sortField: 'description', title: this.translate.instant('DESCRIPTION'), show: (this.navigation.getParamValue('members.description.showFiled', true) && true), displayInList: true, type: 'textValue'},
      { field: 'photo', sortField: 'photo', title: this.translate.instant('PHOTO'), show: (this.navigation.getParamValue('members.photo.showFiled', true) && true), displayInList: true, type: 'imgValue'},
      { field: 'height', sortField: 'height', title: this.translate.instant('HEIGHT'), show: (this.navigation.getParamValue('members.height.showFiled', true) && true), displayInList: true, type: 'numericValue'},
      { field: 'weight', sortField: 'weight', title: this.translate.instant('WEIGHT'), show: (this.navigation.getParamValue('members.weight.showFiled', true) && true), displayInList: true, type: 'numericValue'},
      { field: 'isPublished', sortField: 'isPublished', title: this.translate.instant('ISPUBLISHED'), show: (this.navigation.getParamValue('members.isPublished.showFiled', false) && true), displayInList: true, type: 'booleanValue', filterData: this.navigation.booleanOptions },
      { field: 'createdAt', sortField: 'createdAt', title: this.translate.instant('CREATEDAT'), show: (this.navigation.getParamValue('members.createdAt.showFiled', false) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'creatorUser', title: this.translate.instant('CREATORUSER'), type: 'linkValue', filterData: this.users, show: (this.navigation.getParamValue('members.creatorUser.showFiled', false) && true), displayInList: true, filterField: 'creatorUser.id', displayField: 'username', sortField: 'creatorUser.username', url: 'users' },
      { field: 'modifiedAt', sortField: 'modifiedAt', title: this.translate.instant('MODIFIEDAT'), show: (this.navigation.getParamValue('members.modifiedAt.showFiled', false) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'modifierUser', title: this.translate.instant('MODIFIERUSER'), type: 'linkValue', filterData: this.users, show: (this.navigation.getParamValue('members.modifierUser.showFiled', false) && true), displayInList: true, filterField: 'modifierUser.id', displayField: 'username', sortField: 'modifierUser.username', url: 'users' },
      { field: 'fitnessGoals', title: this.translate.instant('FITNESSGOALS'), type: 'linksValue', filterData: this.fitnessGoals, show: (this.navigation.getParamValue('members.fitnessGoals.showFiled', false) && true), displayInList: true, display: false, filterField: 'fitnessGoals.id', displayField: 'name', sortField: 'fitnessGoals[0].name', url: 'fitness-goals' },
      { field: 'sports', title: this.translate.instant('SPORTS'), type: 'linksValue', filterData: this.sports, show: (this.navigation.getParamValue('members.sports.showFiled', false) && true), displayInList: true, display: false, filterField: 'sports.id', displayField: 'name', sortField: 'sports[0].name', url: 'sports' },
      { field: 'actions', title: this.translate.instant('ACTIONS'), type: 'static', show: true, displayInList: true, sortField: null},
    ];
  }

  format(fieldName: string, value: any) {
    if (fieldName.toLowerCase().indexOf('time') > -1) {
      return moment(value).format('YYYY-MM-DD HH:mm');
    } else if (fieldName.toLowerCase().indexOf('date') > -1) {
      return moment(value).format('YYYY-MM-DD');
    }
    return value;
  }

}
